// The night sky behind the app in dark mode: a field of stars that gently twinkle
// and drift down. It's a single full-screen canvas pinned behind everything
// (pointer-events: none), drawn transparently so the body's blue/purple gradient
// shows through. The animation loop only runs while dark mode is active and the
// tab is visible, so it costs nothing in light mode or in the background.

interface Star {
    x: number
    y: number
    r: number      // radius (also drives parallax drift speed + twinkle base)
    base: number   // base alpha
    tw: number     // twinkle speed
    phase: number  // twinkle phase
    hue: string
}

// White with whispers of blue and purple, to match the gradient sky.
const HUES = ['#ffffff', '#ffffff', '#cdd7ff', '#e7d4ff', '#bcd0ff']

let canvas: HTMLCanvasElement | null = null
let ctx: CanvasRenderingContext2D | null = null
let stars: Star[] = []
let raf = 0
let last = 0
let running = false
let active = false // dark mode on

const seed = (w: number, h: number): void => {
    const count = Math.min(220, Math.round((w * h) / 6000))
    stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.1 + 0.4,
        base: Math.random() * 0.5 + 0.35,
        tw: Math.random() * 1.4 + 0.4,
        phase: Math.random() * Math.PI * 2,
        hue: HUES[Math.floor(Math.random() * HUES.length)],
    }))
}

const resize = (): void => {
    if (!canvas || !ctx) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = window.innerWidth
    const h = window.innerHeight
    canvas.width = w * dpr
    canvas.height = h * dpr
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    seed(w, h)
}

const frame = (t: number): void => {
    if (!ctx || !running) return
    raf = requestAnimationFrame(frame)
    if (t - last < 33) return // ~30fps is plenty for a starfield, and gentle on battery
    const dt = (t - last) / 1000
    last = t

    const w = window.innerWidth
    const h = window.innerHeight
    ctx.clearRect(0, 0, w, h)
    for (const s of stars) {
        s.phase += s.tw * dt
        s.y += s.r * 2 * dt // bigger stars drift a touch faster (parallax)
        if (s.y > h + 2) { s.y = -2; s.x = Math.random() * w }
        ctx.globalAlpha = Math.max(0, s.base * (0.55 + 0.45 * Math.sin(s.phase)))
        ctx.fillStyle = s.hue
        ctx.beginPath()
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fill()
    }
    ctx.globalAlpha = 1
}

const start = (): void => {
    if (running) return
    running = true
    last = performance.now()
    raf = requestAnimationFrame(frame)
}

const stop = (): void => {
    running = false
    cancelAnimationFrame(raf)
}

const create = (): void => {
    canvas = document.createElement('canvas')
    canvas.style.cssText =
        'position:fixed;inset:0;width:100%;height:100%;z-index:0;pointer-events:none;opacity:0;transition:opacity 0.5s ease;'
    document.body.insertBefore(canvas, document.body.firstChild)
    ctx = canvas.getContext('2d')
    resize()
    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop()
        else if (active) start()
    })
}

const show = (): void => {
    active = true
    if (!canvas) create()
    canvas!.style.display = 'block'
    requestAnimationFrame(() => { if (canvas) canvas.style.opacity = '1' })
    if (!document.hidden) start()
}

const hide = (): void => {
    active = false
    stop()
    if (!canvas) return
    canvas.style.opacity = '0'
    setTimeout(() => { if (!active && canvas) canvas.style.display = 'none' }, 500)
}

export default { show, hide }
